import { Attribute } from '@/models/references-classification/attribute';
import { Category } from '@/models/references-classification/category';
import { Characteristic } from '@/models/references-classification/characteristic';
import { ClassificationSummary } from '@/models/references-classification/classification-summary';
import { axiosService } from '.';

export default class ReferencesClassificationService {
    // #region Categories

    public async getClassificationSummary(businessId: number): Promise<ClassificationSummary> {
        return new ClassificationSummary((await axiosService.instance.get(`references-classification/organization/summary/${businessId}`)).data);
    }

    public async getCategories(businessId: number): Promise<Category[]> {
        const response = await axiosService.instance.get(`references-classification/organization/categories/${businessId}`);
        return response.data.map((c: any) => new Category(c));
    }

    public async newCategory(category: Category): Promise<Category[]> {
        const response = await axiosService.instance.post(`references-classification/organization/categories/${category.businessId}/new`, category);
        return response.data.map((c: any) => new Category(c));
    }

    public async updateCategory(category: Category): Promise<Category[]> {
        const response = await axiosService.instance.post(`references-classification/organization/categories/${category.businessId}/update`, category);
        return response.data.map((c: any) => new Category(c));
    }

    public async disableCategory(category: Category): Promise<Category[]> {
        const response = await axiosService.instance.post(`references-classification/organization/categories/${category.businessId}/disable?categoryId=${category.id}`);
        return response.data.map((c: any) => new Category(c));
    }

    public async getTopLevelCategories(businessId: number): Promise<Category[]> {
        const response = await axiosService.instance.get(`references-classification/organization/categories/${businessId}/top-level`);
        return response.data.map((category: any) => new Category(category));
    }

    public async getCategoryById(businessId: number, categoryId: number): Promise<Category> {
        return new Category((await axiosService.instance.get(`references-classification/organization/categories/${businessId}/${categoryId}`)).data);
    }

    public async getCategoriesTreeByCategoryId(businessId: number, categoryId: number): Promise<Category[]> {
        const response = await axiosService.instance.get(`references-classification/organization/categories/${businessId}/${categoryId}/tree`);
        return response.data.map((category: any) => new Category(category));
    }

    public async getCategoriesByParentId(businessId: number, parentCategoryId: number): Promise<Category[]> {
        const response = await axiosService.instance.get(`references-classification/organization/categories/${businessId}/${parentCategoryId}/by-parent-category-id`);
        return response.data.map((category: any) => new Category(category));
    }

    // #endregion Categories

    // #region Characteristics

    public async newCharacteristic(characteristic: Characteristic): Promise<boolean> {
        const response = await axiosService.instance.post(`references-classification/organization/characteristics/new`, characteristic);
        return response.status === 200;
    }

    public async updateCharacteristic(characteristic: Characteristic): Promise<boolean> {
        const response = await axiosService.instance.post(`references-classification/organization/characteristics/update`, characteristic);
        return response.status === 200;
    }

    public async deleteCharacteristic(characteristic: Characteristic): Promise<boolean> {
        const response = await axiosService.instance.post(`references-classification/organization/characteristics/delete`, characteristic);
        return response.status === 200;
    }

    public async getCharacteristics(businessId: number): Promise<Characteristic[]> {
        const response = await axiosService.instance.get(`references-classification/organization/characteristics/${businessId}`);
        return response.data.map((c: any) => new Characteristic(c));
    }

    public async getCharacteristicIdsByValueName(businessId: number | null, name: string): Promise<number[]> {
        return businessId
            ? (await axiosService.instance.get(`references-classification/organization/characteristics/${businessId}/ids?name=${name}`)).data
            : (await axiosService.instance.get(`references-classification/organization/characteristics/ids?name=${name}`)).data;
    }

    // #endregion Characteristics

    // #region Attributes & Values

    public async newAttribute(attribute: Attribute): Promise<boolean> {
        const response = await axiosService.instance.post(`references-classification/organization/sku-attributes/new`, attribute);
        return response.status === 200;
    }

    public async updateAttribute(attribute: Attribute): Promise<boolean> {
        const response = await axiosService.instance.post(`references-classification/organization/sku-attributes/update`, attribute);
        return response.status === 200;
    }

    public async deleteAttribute(attribute: Attribute): Promise<boolean> {
        const response = await axiosService.instance.post(`references-classification/organization/sku-attributes/delete`, attribute);
        return response.status === 200;
    }

    public async getAttributes(businessId: number): Promise<Attribute[]> {
        const response = await axiosService.instance.get(`references-classification/organization/sku-attributes/${businessId}`);
        return response.data.map((attribute: any) => new Attribute(attribute));
    }

    public async getAttributeIdsByValueName(businessId: number | null, name: string): Promise<number[]> {
        return businessId
            ? (await axiosService.instance.get(`references-classification/organization/sku-attributes/${businessId}/ids?name=${name}`)).data
            : (await axiosService.instance.get(`references-classification/organization/sku-attributes/ids?name=${name}`)).data;
    }

    // #endregion Attributes & Values
}

export const referencesClassificationService = new ReferencesClassificationService();
